import { Badge, Button, Card, Spinner, Tooltip } from 'flowbite-react';
import moment from 'moment';
import Pagination from 'rc-pagination';
import React, { SetStateAction, useState } from 'react';
import { AiFillCalendar, AiFillClockCircle, AiFillPhone } from 'react-icons/ai';
import { BsBookmarkCheckFill } from 'react-icons/bs';
import { Img } from 'react-image';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isScheduledOrOngoingSession } from 'shared-utils';
import { useUserContext } from 'src/context/UserProvider';
import { GuestUserDocument, Session, SessionStatus, SessionType, UISession, UserDocument } from 'types';
import FeedbackModal from '../mentorSessions/FeedbackModal';
import MeetingRecordingModal from '../scorecard/MeetingRecordingModal';
import SessionRecordingModal from '../scorecard/SessionRecordingModal';

export enum Step {
	StepOne,
	StepTwo,
}

type Props = {
	data: UISession[];
	sessionDataCategory?: string;
	total?: number;
};

const SessionCards = ({ data, total }: Props) => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const page = Number(searchParams.get('page')) || 1;

	const [sessionJoiningId] = useState<string>('');
	const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState<boolean>(false);
	const [isSessionRecordingModalOpen, setIsSessionRecordingModalOpen] = useState<boolean>(false);
	const handleJoinAsHost = (sessionId: string) => {
		console.log(`/hosts/sessions/live/${sessionId}`);
		navigate(`/hosts/sessions/live/${sessionId}`);
	};
	const [activeSession, setActiveSession] = useState<UISession>();

	const onFeedbackModalOpen = (session: Session) => {
		setIsFeedbackModalOpen(true);
		setActiveSession(session as UISession);
	};

	return (
		<>
			<div className="flex items-center justify-center mb-4">
				<Pagination
					total={total ?? 0}
					pageSize={15}
					current={page || 1}
					showSizeChanger={true}
					showTotal={total => `Showing ${(page - 1) * 15 + 1}-${page * 15} of ${total} sessions`}
					onChange={(p: SetStateAction<any>) => {
						setSearchParams({ page: p });
					}}
				></Pagination>
			</div>
			<div className="flex flex-col gap-4">
				{data.map((session, i) => (
					<div key={session._id}>
						<Card className="dark:bg-[#18222D] dark:!border-[#2c3844]">
							<div className="grid grid-cols-5 items-center justify-center gap-x-[14px]">
								<div className="flex flex-col justify-center gap-2 text-left overflow-hidden">
									<Tooltip content={session.title}>
										<p className="max-w-[260px] truncate text-xl">{session.title}</p>
									</Tooltip>
								</div>

								<div className="flex items-center justify-center">
									<div className="flex flex-col gap-4 whitespace-normal break-words md:min-w-[160px]">
										<p className="text-xl truncate max-w-[200px]">{session.participant?.name}</p>
									</div>
								</div>
								<div className="flex items-center justify-center">
									<div className="flex flex-col gap-2">
										<div className="flex items-center gap-2">
											<AiFillCalendar size={'20px'} className="text-gray-400" />
											<div className="text-sm font-medium text-gray-400">
												{moment(session.startTime).format('DD MMM YYYY, hh:mm a')}
											</div>
										</div>
										<div className="flex items-center gap-2">
											<AiFillClockCircle size={'20px'} className="text-gray-400" />
											<div className="text-sm font-medium text-gray-400">{session.duration} mins</div>
										</div>
										<div className="flex items-center gap-2">
											<AiFillPhone size={'20px'} className="text-gray-400" />
											<div className="text-sm font-medium text-gray-400">{session.participant?.phone}</div>
										</div>
									</div>
								</div>
								<div className="flex items-center justify-center">
									<Badge className="max-w-fit" size="md" color="indigo">
										{session.sessionStatus}
									</Badge>
								</div>
								<div className="row-span-2 flex flex-col items-center justify-center gap-4">
									{session.sessionStatus === SessionStatus.COMPLETED ? (
										<>
											<Button
												color="purplePlaced"
												data-testid={`interviews-mentor-meeting-card-${i}-button-view-recording`}
												onClick={() => {
													setActiveSession(session);
													setIsSessionRecordingModalOpen(true);
												}}
											>
												View Recording
											</Button>

											{!session?.hostFeedback?.length ? (
												<Badge
													className="m-auto w-max cursor-pointer items-center gap-[0px] pb-[3px] pl-[5px] pr-[5px] pt-[3px] hover:underline"
													color="failure"
													onClick={() => onFeedbackModalOpen(session)}
												>
													Provide Feedback
												</Badge>
											) : (
												<Badge
													className="m-auto w-max items-center gap-[0px] pb-[3px] pl-[5px] pr-[5px] pt-[3px]"
													color="success"
													icon={BsBookmarkCheckFill}
													// onClick={() => {
													// 	feedbackProvidedFn(session, session.hostFeedback as HostFeedback);
													// }}
												>
													<span className="ml-1">Feedback Provided</span>
												</Badge>
											)}
										</>
									) : (
										isScheduledOrOngoingSession(session.sessionStatus) && (
											<>
												<Button
													color="purplePlaced"
													data-testid={`interviews-mentor-meeting-card-${i}-button-join`}
													onClick={() => handleJoinAsHost(session._id)}
													disabled={sessionJoiningId === session._id}
													className="px-[10px]"
												>
													{sessionJoiningId === session._id ? <Spinner size="sm" light={true} /> : 'Join'}
												</Button>
											</>
										)
									)}
								</div>
							</div>
						</Card>
					</div>
				))}
				<FeedbackModal
					levelUpParticipant={activeSession?.levelUpParticipant as UserDocument}
					participant={activeSession?.participant as GuestUserDocument}
					mentor={activeSession?.host as UserDocument}
					isOpen={isFeedbackModalOpen}
					setIsOpen={setIsFeedbackModalOpen}
					sessionId={activeSession?._id}
					isMentorSession={activeSession?.type === SessionType.MENTOR_SESSION ? true : false}
				/>

				{activeSession?._id.toString() && (
					<SessionRecordingModal
						isOpen={isSessionRecordingModalOpen}
						onClose={() => setIsSessionRecordingModalOpen(false)}
						sessionId={activeSession?._id}
					/>
				)}
			</div>
		</>
	);
};

export default SessionCards;
